import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import api from "@/lib/api";
import { BLOG_AUTHOR_LABEL, estimateReadMinutes } from "@/lib/blogReadTime";

function SkeletonCard({ index }) {
  return (
    <div className="animate-pulse" style={{ animationDelay: `${index * 80}ms` }}>
      <div className="h-3 w-16 bg-neutral-200 rounded mb-4" />
      <div className="h-5 bg-neutral-200 rounded mb-2 w-4/5" />
      <div className="h-5 bg-neutral-200 rounded mb-4 w-3/5" />
      <div className="h-3 w-28 bg-neutral-100 rounded" />
    </div>
  );
}

function ArticleNumber({ n }) {
  return (
    <div
      className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm"
      style={{ background: "#F0EBE3", color: "#C4A882" }}
    >
      {String(n).padStart(2, "0")}
    </div>
  );
}

export default function BlogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/blog/posts", { params: { limit: 50 } });
        if (!cancelled) setItems(r.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const featured = items[0] ?? null;
  const rest = items.slice(1);

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar />

      {/* Hero header */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d1f0e 60%, #3d2510 100%)",
        }}
      >
        {/* Decorative grid lines */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#FF7A00 1px, transparent 1px), linear-gradient(90deg, #FF7A00 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF7A00]" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-14 sm:pt-20 sm:pb-18">
          <div className="flex items-center gap-3 mb-6">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase"
              style={{ background: "rgba(255,122,0,0.18)", color: "#FF7A00", border: "1px solid rgba(255,122,0,0.3)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF7A00] animate-pulse" />
              Scaffoindia Journal
            </span>
          </div>
          <h1
            className="font-black leading-none tracking-tighter text-white mb-4"
            style={{ fontSize: "clamp(2.6rem, 7vw, 5rem)", letterSpacing: "-0.03em" }}
          >
            Field Notes &<br />
            <span style={{ color: "#FF7A00" }}>Industry Insights</span>
          </h1>
          <p className="text-neutral-400 text-base sm:text-lg max-w-xl leading-relaxed mt-4">
            Practical knowledge from the scaffolding industry — safety, sourcing, site management, and more.
          </p>
          <div className="mt-8 flex items-center gap-4 text-sm text-neutral-500">
            <span>{loading ? "—" : items.length} articles</span>
            <span className="w-1 h-1 rounded-full bg-neutral-600" />
            <span>Updated regularly</span>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16">

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-neutral-200">
                <SkeletonCard index={i} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <div
              className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center font-black text-2xl"
              style={{ background: "#F0EBE3", color: "#C4A882" }}
            >
              —
            </div>
            <p className="text-neutral-500 text-lg font-medium">No articles yet.</p>
            <p className="text-neutral-400 text-sm mt-1">Check back soon — new posts are on the way.</p>
          </div>
        ) : (
          <>
            {/* Featured article */}
            {featured && (
              <div className="mb-10">
                <p
                  className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4"
                  style={{ color: "#FF7A00" }}
                >
                  Featured
                </p>
                <Link
                  to={`/blog/${encodeURIComponent(featured.slug)}`}
                  data-testid={`blog-list-link-${featured.slug}`}
                  onMouseEnter={() => setHoveredId(featured.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className="group block rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A00] focus-visible:ring-offset-2"
                  style={{
                    background: hoveredId === featured.id
                      ? "linear-gradient(135deg, #1a1a1a 0%, #2a1800 100%)"
                      : "linear-gradient(135deg, #2d1f0e 0%, #1a1a1a 100%)",
                    transition: "background 0.3s ease",
                  }}
                >
                  <div className="p-8 sm:p-10 lg:p-12 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-10">
                    <div className="flex-1 min-w-0">
                      <div
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-5"
                        style={{ background: "rgba(255,122,0,0.2)", color: "#FF7A00" }}
                      >
                        Latest
                      </div>
                      <h2
                        className="font-black text-white leading-tight tracking-tight mb-4 group-hover:text-[#FF7A00] transition-colors duration-200"
                        style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)" }}
                      >
                        {featured.title}
                      </h2>
                      {featured.excerpt && (
                        <p className="text-neutral-400 text-sm sm:text-base leading-relaxed line-clamp-2 mb-5">
                          {featured.excerpt}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span>{estimateReadMinutes(featured.body, featured.excerpt)} min read</span>
                        <span className="w-1 h-1 rounded-full bg-neutral-600" />
                        <span>{BLOG_AUTHOR_LABEL}</span>
                      </div>
                    </div>
                    <div
                      className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center font-black text-2xl"
                      style={{ background: "rgba(255,122,0,0.15)", color: "#FF7A00" }}
                    >
                      01
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Rest of articles grid */}
            {rest.length > 0 && (
              <>
                <p
                  className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5"
                  style={{ color: "#FF7A00" }}
                >
                  All Articles
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {rest.map((p, i) => {
                    const mins = estimateReadMinutes(p.body, p.excerpt);
                    const isHovered = hoveredId === p.id;
                    return (
                      <Link
                        key={p.id}
                        to={`/blog/${encodeURIComponent(p.slug)}`}
                        data-testid={`blog-list-link-${p.slug}`}
                        onMouseEnter={() => setHoveredId(p.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        className="group block rounded-2xl border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A00] focus-visible:ring-offset-2 overflow-hidden"
                        style={{
                          background: isHovered ? "#fff" : "#FDFCF9",
                          borderColor: isHovered ? "#FF7A00" : "#E8E3D9",
                          transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                          boxShadow: isHovered
                            ? "0 8px 28px rgba(255,122,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
                            : "0 1px 4px rgba(0,0,0,0.04)",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div className="p-6">
                          <ArticleNumber n={i + 2} />
                          <h3
                            className="font-bold leading-snug mt-4 mb-3 transition-colors duration-200"
                            style={{
                              fontSize: "1rem",
                              color: isHovered ? "#FF7A00" : "#111",
                            }}
                          >
                            {p.title}
                          </h3>
                          {p.excerpt && (
                            <p className="text-neutral-500 text-sm leading-relaxed line-clamp-2 mb-4">
                              {p.excerpt}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-auto pt-2 border-t border-neutral-100">
                            <div className="flex items-center gap-2 text-xs text-neutral-400">
                              <span>{mins} min read</span>
                              <span className="w-1 h-1 rounded-full bg-neutral-300" />
                              <span>{BLOG_AUTHOR_LABEL}</span>
                            </div>
                            <span
                              className="text-xs font-bold transition-colors duration-200"
                              style={{ color: isHovered ? "#FF7A00" : "#C4B9A8" }}
                            >
                              Read →
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Footer strip */}
      <div
        className="border-t mt-8 py-8 text-center"
        style={{ borderColor: "#E8E3D9", background: "#F0EBE3" }}
      >
        <p className="text-xs text-neutral-400 tracking-widest uppercase font-semibold">
          Scaffoindia · Field Notes &amp; Industry Insights
        </p>
      </div>
    </div>
  );
}
